import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppModeService } from 'src/app/services/app-mode.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CartItem, CartService } from 'src/app/services/cart/cart.service';
import { combineLatest, map } from 'rxjs';
import { OrderService } from 'src/app/services/order/order.service';
import {
  Address,
  AddressService,
} from 'src/app/services/address/address.service';

export interface CountryCode {
  name: string;
  code: string;
}

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css'],
})
export class CheckoutComponent implements OnInit {
  authStatus: 'guest' | 'loggedIn' = 'guest';
  authModal = false;
  summaryOpen = false;
  isSubmitting = false;
  isSavingAddress = false;
  countryDropdownOpen = false;

  userAddresses: Address[] = [];
  selectedAddressId: string | null = null;
  showAddressForm = false;
  addressForm!: FormGroup;

  readonly items$ = this.cartService.items$;
  readonly discount$ = this.cartService.discount$;
  readonly subtotal$ = this.cartService.total$;
  readonly totalToPay$ = combineLatest([this.subtotal$, this.discount$]).pipe(
    map(([subtotal, discount]) => Math.max(0, subtotal - discount)),
  );

  guestForm!: FormGroup;

  paymentOptions = [{ value: 'CASH_ON_DELIVERY', labelKey: 'PAYMENT_CASH' }];

  countries: CountryCode[] = [
    { name: 'Egypt', code: '+20' },
    { name: 'Saudi Arabia', code: '+966' },
    { name: 'UAE', code: '+971' },
    { name: 'Kuwait', code: '+965' },
    { name: 'Qatar', code: '+974' },
    { name: 'Jordan', code: '+962' },
    { name: 'USA', code: '+1' },
    { name: 'UK', code: '+44' },
    { name: 'France', code: '+33' },
  ];

  selectedCountry: CountryCode = this.countries[0];
  selectedPayment = 'CASH_ON_DELIVERY';
  authInstructions = '';

  constructor(
    private authService: AuthService,
    private router: Router,
    private readonly cartService: CartService,
    public readonly appModeService: AppModeService,
    private fb: FormBuilder,
    private orderService: OrderService,
    private addressService: AddressService,
  ) {}

  ngOnInit(): void {
    if (this.authService.getAccessToken()) {
      this.authStatus = 'loggedIn';
      this.authModal = false;
      this.loadAddresses();
    } else {
      this.authStatus = 'guest';
      this.authModal = true;
    }

    this.guestForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern(/^\d{7,12}$/)],
      ],
      streetAddress: ['', [Validators.required]],
      region: ['', [Validators.required]],
      city: ['', [Validators.required]],
      postalCode: [''],
      paymentMethod: ['CASH_ON_DELIVERY', [Validators.required]],
      instructions: [''],
    });

    this.addressForm = this.fb.group({
      streetAddress: ['', Validators.required],
      region: ['', Validators.required],
      city: ['', Validators.required],
      postalCode: [''],
    });
  }

  loadAddresses() {
    this.addressService.getAddresses().subscribe({
      next: (res: Address[]) => {
        this.userAddresses = res;
        if (res.length > 0) {
          this.selectedAddressId = res[0].id;
          this.showAddressForm = false;
        } else {
          this.showAddressForm = true;
        }
      },
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.countryDropdownOpen = false;
  }

  toggleSummary() {
    this.summaryOpen = !this.summaryOpen;
  }
  continueAsGuest() {
    this.authModal = false;
  }
  goToLogin() {
    this.router.navigateByUrl('/register');
  }
  trackByItem(_: number, item: CartItem): string {
    return item.id;
  }

  selectAddress(id: string) {
    this.selectedAddressId = id;
    this.showAddressForm = false;
  }

  toggleAddressForm() {
    this.showAddressForm = !this.showAddressForm;
    if (this.showAddressForm) {
      this.selectedAddressId = null;
      this.addressForm.reset();
    } else if (this.userAddresses.length > 0) {
      // revert to first address when closing form
      this.selectedAddressId = this.userAddresses[0].id;
    }
  }

  /** Optional: only called if the user explicitly wants this address saved to their account. */
  saveNewAddress() {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    this.isSavingAddress = true;
    this.addressService.createAddress(this.addressForm.value).subscribe({
      next: (newAddr: Address) => {
        this.userAddresses = [...this.userAddresses, newAddr];
        this.selectedAddressId = newAddr.id;
        this.showAddressForm = false;
        this.addressForm.reset();
        this.isSavingAddress = false;
      },
      error: () => {
        this.isSavingAddress = false;
      },
    });
  }

  selectPayment(value: string) {
    this.selectedPayment = value;
    this.guestForm.get('paymentMethod')?.setValue(value);
  }

  toggleCountryDropdown(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.countryDropdownOpen = !this.countryDropdownOpen;
  }

  selectCountry(country: CountryCode, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.selectedCountry = country;
    this.countryDropdownOpen = false;
  }

  isInvalid(field: string, form: FormGroup = this.guestForm): boolean {
    const ctrl = form.get(field);
    return !!(ctrl?.invalid && ctrl?.touched);
  }

  submitAuthOrder(items: CartItem[]) {
    // Case 1: an existing saved address is selected -> send its id.
    // Case 2: the address form is open (typed but not saved) -> validate it and send it inline.
    // Case 3: neither -> open the form and stop, so the user can fill it in.
    if (!this.selectedAddressId) {
      this.showAddressForm = true;
    }

    if (this.showAddressForm) {
      if (this.addressForm.invalid) {
        this.addressForm.markAllAsTouched();
        return;
      }
    } else if (!this.selectedAddressId) {
      return;
    }

    const payload: any = {
      items: items.map((item) => {
        const [colorCode, size] = item.variant
          .split(' / ')
          .map((s: string) => s.trim());
        const productId = item.id.split('-' + colorCode)[0];
        return {
          productId,
          colorName: colorCode,
          colorCode,
          size,
          quantity: item.quantity,
          imageUrl: item.imageUrl,
        };
      }),
      paymentMethod: this.selectedPayment,
      instructions: this.authInstructions,
    };

    if (this.showAddressForm) {
      // Typed address, not saved to the account - send inline like the guest flow.
      const v = this.addressForm.value;
      payload.address = {
        streetAddress: v.streetAddress,
        region: v.region,
        city: v.city,
        postalCode: v.postalCode || '',
      };
    } else {
      payload.addressId = this.selectedAddressId;
    }

    this.isSubmitting = true;
    this.orderService.placeAuthOrder(payload).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        this.cartService.clearCart();

        this.router.navigate(['/order-success', res.id]);
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }

  submitGuestOrder(items: CartItem[]) {
    if (this.guestForm.invalid) {
      this.guestForm.markAllAsTouched();
      return;
    }

    const v = this.guestForm.value;
    const fullPhone = `${this.selectedCountry.code}${v.phoneNumber}`;

    const payload = {
      items: items.map((item) => {
        const [colorCode, size] = item.variant
          .split(' / ')
          .map((s: string) => s.trim());
        const productId = item.id.split('-' + colorCode)[0];
        return {
          productId,
          colorName: colorCode,
          colorCode,
          size,
          quantity: item.quantity,
          imageUrl: item.imageUrl,
        };
      }),
      contact: { name: v.name, email: v.email, phone: fullPhone },
      address: {
        streetAddress: v.streetAddress,
        region: v.region,
        city: v.city,
        postalCode: v.postalCode || '',
      },
      paymentMethod: v.paymentMethod,
      instructions: v.instructions || '',
    };

    this.isSubmitting = true;
    this.orderService.placeGuestOrder(payload).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        this.cartService.clearCart();

        this.router.navigate(['/order-success', res.id]);
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }
}
